import React, { useState, useCallback, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import {
  type NodeProps,
  type Node,
  Handle,
  Position,
  useReactFlow,
  useNodes,
  useEdges,
} from "@xyflow/react";
import {
  Globe,
  Trash2,
  Settings,
  X,
  AlertTriangle,
  Loader2,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Plus,
  FileText,
  FileImage,
  File,
} from "lucide-react";
import { AvailableVariable } from "../../common/ui/types";
import { formatDragVariable } from "../../utils/variableHelper";
import { getAllAncestorNodes } from "../../utils/workflowHelpers";
import { getNodeValidationStatus } from "../../utils/nodeValidation";
import { NodeDataSidebar } from "../../common/shared/NodeDataSidebar";

const FILE_TYPE_OPTIONS = ["PDF", "DOCX", "XLSX", "PPTX", "CSV", "IMAGE"];

export const WebScraperNode: React.FC<NodeProps<Node<any>>> = ({
  id,
  data,
  selected,
}) => {
  const [seedUrl, setSeedUrl] = useState<string>(data?.seed_url || "");

  const [maxDepth, setMaxDepth] = useState<string>(data?.max_depth?.toString() || "");
  const [maxPages, setMaxPages] = useState<string>(
    data?.max_pages?.toString() || "",
  );
  const [maxFileSizeBytes, setMaxFileSizeBytes] = useState<string>(
    data?.max_file_size_bytes?.toString() || "",
  );
  const [concurrency, setConcurrency] = useState<string>(
    data?.concurrency?.toString() || "",
  );
  const [requestDelayMs, setRequestDelayMs] = useState<string>(
    data?.request_delay_ms?.toString() || "",
  );
  const [allowedFileTypes, setAllowedFileTypes] = useState<string[]>(
    data?.allowed_file_types || [],
  );
  const [usePlaywrightFallback, setUsePlaywrightFallback] = useState<boolean>(
    data?.use_playwright_fallback ?? false,
  );
  const [respectRobotsTxt, setRespectRobotsTxt] = useState<boolean>(
    data?.respect_robots_txt ?? true,
  );
  const [crawlSubdomains, setCrawlSubdomains] = useState<boolean>(
    data?.crawl_subdomains ?? false,
  );
  const [enableChangeDetection, setEnableChangeDetection] = useState<boolean>(
    data?.enable_change_detection ?? false,
  );
  const [pollTimeoutMs, setPollTimeoutMs] = useState<string>(
    data?.poll_timeout_ms?.toString() || "300000",
  );
  const [pollIntervalMs, setPollIntervalMs] = useState<string>(
    data?.poll_interval_ms?.toString() || "5000",
  );
  const [customHeaders, setCustomHeaders] = useState<
    Array<{ id: string; key: string; value: string }>
  >(
    Object.entries(data?.custom_headers || {}).map(([k, v]) => ({
      id: Math.random().toString(36).substring(2, 9),
      key: k,
      value: String(v),
    })),
  );
  const [localStorageEntries, setLocalStorageEntries] = useState<
    Array<{ id: string; key: string; value: string }>
  >(
    Object.entries(data?.local_storage || {}).map(([k, v]) => ({
      id: Math.random().toString(36).substring(2, 9),
      key: k,
      value: String(v),
    })),
  );
  const [useLoginConfig, setUseLoginConfig] = useState<boolean>(
    !!data?.login_config?.login_url,
  );
  const [loginUrl, setLoginUrl] = useState<string>(
    data?.login_config?.login_url || "",
  );
  const [loginUsernameSelector, setLoginUsernameSelector] = useState<string>(
    data?.login_config?.username_selector || "",
  );
  const [loginPasswordSelector, setLoginPasswordSelector] = useState<string>(
    data?.login_config?.password_selector || "",
  );
  const [loginSubmitSelector, setLoginSubmitSelector] = useState<string>(
    data?.login_config?.submit_selector || "",
  );
  const [loginUsername, setLoginUsername] = useState<string>(
    data?.login_config?.username || "",
  );
  const [loginPassword, setLoginPassword] = useState<string>(
    data?.login_config?.password || "",
  );
  const [loginSuccessUrlPattern, setLoginSuccessUrlPattern] = useState<string>(
    data?.login_config?.success_url_pattern || "",
  );
  const [loginWaitForSelector, setLoginWaitForSelector] = useState<string>(
    data?.login_config?.wait_for_selector || "",
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDraggingOverSeedUrl, setIsDraggingOverSeedUrl] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [expandedPageIdx, setExpandedPageIdx] = useState<number | null>(null);
  const [expandedDiffKey, setExpandedDiffKey] = useState<string | null>(null);

  const { setNodes, deleteElements } = useReactFlow();
  const nodes = useNodes();
  const edges = useEdges();

  const executionStatus = data?.executionStatus || null;
  const isExecuting = executionStatus === "running";
  const executionSuccess = executionStatus === "success";
  const executionError = executionStatus === "error";

  const stopPropagation = useCallback(
    (e: React.MouseEvent | React.FocusEvent) => e.stopPropagation(),
    [],
  );

  const handleDeleteNode = useCallback(() => {
    deleteElements({ nodes: [{ id }] });
  }, [deleteElements, id]);

  const extractNodeData = useCallback((nodeData: any) => {
    if (!nodeData || typeof nodeData !== "object")
      return { inputs: {}, outputs: {} };
    const outputs = nodeData.outputs || {};
    const inputs: Record<string, any> = {};
    Object.entries(nodeData).forEach(([key, value]) => {
      if (key !== "outputs") inputs[key] = value;
    });
    return { inputs, outputs };
  }, []);

  const previousNodes = useMemo(() => {
    const ancestorNodes = getAllAncestorNodes(id, nodes, edges);
    return ancestorNodes.map((sourceNode) => {
      const { inputs, outputs } = extractNodeData(sourceNode.data);
      return {
        nodeId: sourceNode.id,
        nodeName: sourceNode.data?.name || sourceNode.id,
        nodeType: sourceNode.type || "unknown",
        inputs,
        outputs,
      };
    }) as AvailableVariable[];
  }, [id, nodes, edges, extractNodeData]);

  const validationStatus = useMemo(() => {
    const tempNode: Node<any> = {
      id,
      type: "WEB-SCRAPER",
      position: { x: 0, y: 0 },
      data: { ...data, seed_url: seedUrl },
    };
    return getNodeValidationStatus(tempNode, edges, nodes);
  }, [id, data, seedUrl, edges, nodes]);

  const statusConfig = useMemo(() => {
    return seedUrl
      ? { bg: "bg-cyan-500", text: "Configured" }
      : { bg: "bg-gray-400", text: "Not Configured" };
  }, [seedUrl]);

  useEffect(() => {
    const headersObj = customHeaders.reduce<Record<string, string>>(
      (acc, { key, value }) => {
        if (key) acc[key] = value;
        return acc;
      },
      {},
    );
    const localStorageObj = localStorageEntries.reduce<Record<string, string>>(
      (acc, { key, value }) => {
        if (key) acc[key] = value;
        return acc;
      },
      {},
    );
    const loginConfigValue =
      useLoginConfig && loginUrl
        ? {
            login_url: loginUrl,
            username_selector: loginUsernameSelector,
            password_selector: loginPasswordSelector,
            submit_selector: loginSubmitSelector,
            username: loginUsername,
            password: loginPassword,
            ...(loginSuccessUrlPattern ? { success_url_pattern: loginSuccessUrlPattern } : {}),
            ...(loginWaitForSelector ? { wait_for_selector: loginWaitForSelector } : {}),
          }
        : undefined;

    setNodes((nds) =>
      nds.map((node) =>
        node.id === id
          ? {
              ...node,
              data: {
                ...node.data,
                seed_url: seedUrl,
                max_depth: maxDepth !== "" ? Number(maxDepth) : undefined,
                max_pages: maxPages !== "" ? Number(maxPages) : undefined,
                max_file_size_bytes: maxFileSizeBytes !== "" ? Number(maxFileSizeBytes) : undefined,
                concurrency: concurrency !== "" ? Number(concurrency) : undefined,
                request_delay_ms: requestDelayMs !== "" ? Number(requestDelayMs) : undefined,
                allowed_file_types: allowedFileTypes,
                use_playwright_fallback: usePlaywrightFallback,
                respect_robots_txt: respectRobotsTxt,
                crawl_subdomains: crawlSubdomains,
                enable_change_detection: enableChangeDetection,
                poll_timeout_ms: pollTimeoutMs !== "" ? Number(pollTimeoutMs) : 300000,
                poll_interval_ms: pollIntervalMs !== "" ? Number(pollIntervalMs) : 5000,
                custom_headers: headersObj,
                local_storage: localStorageObj,
                login_config: loginConfigValue,
              },
            }
          : node,
      ),
    );
  }, [
    seedUrl,
    maxDepth,
    maxPages,
    maxFileSizeBytes,
    concurrency,
    requestDelayMs,
    allowedFileTypes,
    usePlaywrightFallback,
    respectRobotsTxt,
    crawlSubdomains,
    enableChangeDetection,
    pollTimeoutMs,
    pollIntervalMs,
    customHeaders,
    localStorageEntries,
    useLoginConfig,
    loginUrl,
    loginUsernameSelector,
    loginPasswordSelector,
    loginSubmitSelector,
    loginUsername,
    loginPassword,
    loginSuccessUrlPattern,
    loginWaitForSelector,
    id,
    setNodes,
  ]);

  const handleDragStart = (
    e: React.DragEvent,
    variable: string,
    nodeName: string,
    type: "input" | "output",
  ) => {
    e.stopPropagation();
    const fullVariable = formatDragVariable(nodeName, variable, type);
    e.dataTransfer.effectAllowed = "copy";
    e.dataTransfer.setData("text/plain", fullVariable);
  };

  const handleSeedUrlDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const variable = e.dataTransfer.getData("text/plain");
    if (variable) setSeedUrl(variable);
    setIsDraggingOverSeedUrl(false);
  };

  const toggleFileType = (ft: string) => {
    setAllowedFileTypes((prev) =>
      prev.includes(ft) ? prev.filter((x) => x !== ft) : [...prev, ft],
    );
  };

  const addCustomHeader = () =>
    setCustomHeaders((prev) => [...prev, { id: Math.random().toString(36).substring(2, 9), key: "", value: "" }]);
  const removeCustomHeader = (idx: number) =>
    setCustomHeaders((prev) => prev.filter((_, i) => i !== idx));
  const updateCustomHeader = (
    idx: number,
    field: "key" | "value",
    val: string,
  ) => {
    setCustomHeaders((prev) =>
      prev.map((h, i) => (i === idx ? { ...h, [field]: val } : h)),
    );
  };

  const addLocalStorageEntry = () =>
    setLocalStorageEntries((prev) => [
      ...prev,
      { id: Math.random().toString(36).substring(2, 9), key: "", value: "" },
    ]);
  const removeLocalStorageEntry = (idx: number) =>
    setLocalStorageEntries((prev) => prev.filter((_, i) => i !== idx));
  const updateLocalStorageEntry = (
    idx: number,
    field: "key" | "value",
    val: string,
  ) => {
    setLocalStorageEntries((prev) =>
      prev.map((e, i) => (i === idx ? { ...e, [field]: val } : e)),
    );
  };

  const hasOutputs = data?.outputs && Object.keys(data.outputs).length > 0;

  return (
    <div
      className={`relative group bg-cyan-50/80 backdrop-blur-xl border-2 rounded-xl p-4 min-w-[280px] max-w-[320px] shadow-sm transition-all ${
        !validationStatus.isValid
          ? "border-red-500 shadow-red-200"
          : isExecuting
            ? "border-blue-500 shadow-lg shadow-blue-200 animate-pulse"
            : executionSuccess
              ? "border-green-500 shadow-md shadow-green-200"
              : executionError
                ? "border-red-500 shadow-md shadow-red-200"
                : selected
                  ? "border-cyan-500 shadow-md"
                  : "border-cyan-300 hover:border-cyan-400"
      }`}
    >
      {isExecuting && (
        <div className="absolute inset-0 bg-blue-500/10 backdrop-blur-[1px] rounded-xl flex items-center justify-center z-10">
          <div className="bg-white rounded-full p-3 shadow-lg">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        </div>
      )}
      {executionSuccess && (
        <div className="absolute -top-3 -right-4 z-30 pointer-events-none">
          <div className="bg-green-500 rounded-full p-1.5 border-2 border-white">
            <CheckCircle className="w-4 h-4 text-white" />
          </div>
        </div>
      )}
      {executionError && (
        <div className="absolute -top-3 -right-4 z-30 pointer-events-none">
          <div className="bg-red-500 rounded-full p-1.5 border-2 border-white">
            <AlertTriangle className="w-4 h-4 text-white" />
          </div>
        </div>
      )}

      <Handle
        type="target"
        position={Position.Left}
        className="w-3 h-3 bg-cyan-500 border-2 border-white"
        style={{ boxShadow: "0 0 0 4px rgba(6, 182, 212, 0.25)" }}
      />

      {/* Canvas card header */}
      <div className="flex items-start justify-between mb-3 cursor-move">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg shadow-sm bg-cyan-100">
            <Globe className="w-4 h-4 text-cyan-700" />
          </div>
          <div>
            <h3 className="font-medium text-gray-800 text-sm">
              {data?.name || "WebScraperNode"}
            </h3>
            <div className="flex items-center gap-1.5 mt-0.5">
              <div className={`w-1.5 h-1.5 rounded-full ${statusConfig.bg}`} />
              <p className="text-xs text-gray-600">{statusConfig.text}</p>
            </div>
          </div>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteNode();
          }}
          className="text-gray-500 hover:text-gray-700 p-1 rounded transition"
          title="Delete"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* URL preview */}
      <div
        className="p-2.5 bg-white/80 border border-cyan-200 rounded-lg text-xs shadow-inner mb-3"
        onClick={stopPropagation}
      >
        {seedUrl ? (
          <div className="flex items-start gap-1.5">
            <span className="text-gray-500 shrink-0">URL:</span>
            <span className="text-cyan-700 font-mono truncate">{seedUrl}</span>
          </div>
        ) : (
          <div className="text-gray-400 italic">No URL configured</div>
        )}
      </div>

      <div className="mt-1" onClick={stopPropagation}>
        <button
          onClick={() => setIsModalOpen(true)}
          className="w-full py-2.5 rounded-lg bg-cyan-600 text-white text-xs font-medium flex justify-center gap-2 shadow-sm hover:bg-cyan-700 transition-colors"
        >
          <Settings className="w-3.5 h-3.5" /> Configure Web Scraper
        </button>
      </div>

      <Handle
        type="source"
        position={Position.Right}
        className="w-3 h-3 bg-cyan-500 border-2 border-white"
        style={{ boxShadow: "0 0 0 4px rgba(6, 182, 212, 0.25)" }}
      />

      {/* Config Modal */}
      {isModalOpen &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4"
            onClick={() => setIsModalOpen(false)}
          >
            <div
              className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col"
              onClick={stopPropagation}
            >
              {/* Modal header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-cyan-50 rounded">
                    <Globe className="w-5 h-5 text-cyan-600" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">
                      Configure Web Scraper
                    </h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Set the seed URL and optional crawl settings
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-hidden flex divide-x divide-gray-200">
                {/* Left: ancestor node variables */}
                <div className="w-[340px] shrink-0 overflow-hidden">
                  <NodeDataSidebar
                    availableVariables={previousNodes}
                    onDragStart={handleDragStart}
                  />
                </div>

                {/* Center: form */}
                <div className="flex-1 overflow-y-auto bg-white p-6 space-y-5">
                  {/* Validation errors */}
                  {!validationStatus.isValid && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
                        <div>
                          <h4 className="text-sm font-semibold text-red-900 mb-2">
                            Configuration Issues
                          </h4>
                          <ul className="text-sm text-red-700 space-y-1 list-disc list-inside">
                            {validationStatus.errors.map((err, i) => (
                              <li key={i}>{err}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Seed URL — only required field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Seed URL <span className="text-red-500">*</span>
                    </label>
                    <div
                      onDrop={handleSeedUrlDrop}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsDraggingOverSeedUrl(true);
                      }}
                      onDragLeave={() => setIsDraggingOverSeedUrl(false)}
                      className={`relative rounded-lg border-2 transition-all ${
                        isDraggingOverSeedUrl
                          ? "border-cyan-400 bg-cyan-50"
                          : "border-transparent"
                      }`}
                    >
                      <input
                        type="text"
                        value={seedUrl}
                        onChange={(e) => setSeedUrl(e.target.value)}
                        placeholder="https://docs.example.com  or drag a variable"
                        className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:border-cyan-400 focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition"
                      />
                      {seedUrl.startsWith("{{") && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] bg-cyan-100 text-cyan-700 px-1.5 py-0.5 rounded font-mono pointer-events-none">
                          variable
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      Starting URL for the crawl. Drag a variable reference from
                      the left panel.
                    </p>
                  </div>

                  {/* Advanced Settings (collapsible) */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setAdvancedOpen((v) => !v)}
                      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-sm font-medium text-gray-700"
                    >
                      <span>Advanced Settings</span>
                      {advancedOpen ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </button>

                    {advancedOpen && (
                      <div className="p-4 space-y-5">
                        {/* Number inputs */}
                        <div className="grid grid-cols-2 gap-4">
                          {(
                            [
                              {
                                label: "Max Depth",
                                val: maxDepth,
                                setter: setMaxDepth,
                                placeholder: "unlimited",
                              },
                              {
                                label: "Max Pages",
                                val: maxPages,
                                setter: setMaxPages,
                                placeholder: "unlimited",
                              },
                              {
                                label: "Max File Size (bytes)",
                                val: maxFileSizeBytes,
                                setter: setMaxFileSizeBytes,
                                placeholder: "unlimited",
                              },
                              {
                                label: "Concurrency",
                                val: concurrency,
                                setter: setConcurrency,
                                placeholder: "engine default",
                              },
                              {
                                label: "Request Delay (ms)",
                                val: requestDelayMs,
                                setter: setRequestDelayMs,
                                placeholder: "0",
                              },
                              {
                                label: "Poll Timeout (ms)",
                                val: pollTimeoutMs,
                                setter: setPollTimeoutMs,
                                placeholder: "300000",
                              },
                              {
                                label: "Poll Interval (ms)",
                                val: pollIntervalMs,
                                setter: setPollIntervalMs,
                                placeholder: "5000",
                              },
                            ] as const
                          ).map(({ label, val, setter, placeholder }) => (
                            <div key={label}>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                {label}
                              </label>
                              <input
                                type="number"
                                value={val}
                                onChange={(e) =>
                                  (
                                    setter as React.Dispatch<
                                      React.SetStateAction<string>
                                    >
                                  )(e.target.value)
                                }
                                placeholder={placeholder}
                                min={0}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none"
                              />
                            </div>
                          ))}
                        </div>

                        {/* Allowed File Types */}
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-2">
                            Allowed File Types{" "}
                            <span className="text-gray-400 font-normal">
                              (empty = all)
                            </span>
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {FILE_TYPE_OPTIONS.map((ft) => (
                              <button
                                key={ft}
                                type="button"
                                onClick={() => toggleFileType(ft)}
                                className={`px-3 py-1 text-xs rounded-full border font-medium transition-colors ${
                                  allowedFileTypes.includes(ft)
                                    ? "bg-cyan-100 border-cyan-400 text-cyan-700"
                                    : "bg-white border-gray-300 text-gray-600 hover:border-cyan-300"
                                }`}
                              >
                                {ft}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Toggle Switches */}
                        <div className="space-y-3">
                          {(
                            [
                              {
                                label: "Use Playwright Fallback",
                                val: usePlaywrightFallback,
                                setter: setUsePlaywrightFallback,
                                desc: "For JS-heavy pages",
                              },
                              {
                                label: "Respect robots.txt",
                                val: respectRobotsTxt,
                                setter: setRespectRobotsTxt,
                                desc: "Honour robots.txt rules",
                              },
                              {
                                label: "Crawl Subdomains",
                                val: crawlSubdomains,
                                setter: setCrawlSubdomains,
                                desc: "Follow subdomain links",
                              },
                            ] as const
                          ).map(({ label, val, setter, desc }) => (
                            <div
                              key={label}
                              className="flex items-center justify-between"
                            >
                              <div>
                                <div className="text-xs font-medium text-gray-700">
                                  {label}
                                </div>
                                <div className="text-xs text-gray-400">
                                  {desc}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() =>
                                  (
                                    setter as React.Dispatch<
                                      React.SetStateAction<boolean>
                                    >
                                  )(!val)
                                }
                                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                                  val ? "bg-cyan-500" : "bg-gray-300"
                                }`}
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                                    val ? "translate-x-4" : "translate-x-1"
                                  }`}
                                />
                              </button>
                            </div>
                          ))}
                        </div>

                        {/* Change Detection toggle */}
                        <div className={`rounded-lg border p-3 transition-colors ${enableChangeDetection ? "border-indigo-300 bg-indigo-50" : "border-gray-200 bg-gray-50"}`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-xs font-semibold text-gray-800">
                                Enable Change Detection
                              </div>
                              <div className="text-[11px] text-gray-500 mt-0.5">
                                Compares this run against the previous run and emits diff outputs
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => setEnableChangeDetection((v) => !v)}
                              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0 ml-3 ${
                                enableChangeDetection ? "bg-indigo-600" : "bg-gray-300"
                              }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                                  enableChangeDetection ? "translate-x-4" : "translate-x-1"
                                }`}
                              />
                            </button>
                          </div>
                        </div>

                        {/* Custom Headers */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-xs font-medium text-gray-600">
                              Custom Headers
                            </label>
                            <button
                              type="button"
                              onClick={addCustomHeader}
                              className="text-xs text-cyan-600 hover:text-cyan-700 flex items-center gap-1"
                            >
                              <Plus className="w-3 h-3" /> Add Header
                            </button>
                          </div>
                          {customHeaders.length === 0 ? (
                            <div className="text-xs text-gray-400 italic">
                              No custom headers
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {customHeaders.map((h, i) => (
                                <div key={h.id} className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    placeholder="Header name"
                                    value={h.key}
                                    onChange={(e) =>
                                      updateCustomHeader(
                                        i,
                                        "key",
                                        e.target.value,
                                      )
                                    }
                                    className="flex-1 px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-cyan-500 outline-none"
                                  />
                                  <input
                                    type="text"
                                    placeholder="Value"
                                    value={h.value}
                                    onChange={(e) =>
                                      updateCustomHeader(
                                        i,
                                        "value",
                                        e.target.value,
                                      )
                                    }
                                    className="flex-1 px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-cyan-500 outline-none"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removeCustomHeader(i)}
                                    className="text-gray-400 hover:text-red-500 transition-colors"
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Local Storage (Playwright only) */}
                        {usePlaywrightFallback && (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className="text-xs font-medium text-gray-600">
                                Local Storage{" "}
                                <span className="text-gray-400 font-normal">
                                  (Playwright only)
                                </span>
                              </label>
                              <button
                                type="button"
                                onClick={addLocalStorageEntry}
                                className="text-xs text-cyan-600 hover:text-cyan-700 flex items-center gap-1"
                              >
                                <Plus className="w-3 h-3" /> Add Entry
                              </button>
                            </div>
                            {localStorageEntries.length === 0 ? (
                              <div className="text-xs text-gray-400 italic">
                                No local storage entries
                              </div>
                            ) : (
                              <div className="space-y-2">
                                {localStorageEntries.map((e, i) => (
                                  <div key={e.id} className="flex items-center gap-2">
                                    <input
                                      type="text"
                                      placeholder="Key"
                                      value={e.key}
                                      onChange={(ev) =>
                                        updateLocalStorageEntry(i, "key", ev.target.value)
                                      }
                                      className="flex-1 px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-cyan-500 outline-none"
                                    />
                                    <input
                                      type="text"
                                      placeholder="Value"
                                      value={e.value}
                                      onChange={(ev) =>
                                        updateLocalStorageEntry(i, "value", ev.target.value)
                                      }
                                      className="flex-1 px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-cyan-500 outline-none"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => removeLocalStorageEntry(i)}
                                      className="text-gray-400 hover:text-red-500 transition-colors"
                                    >
                                      <X className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Form-based Login — works with or without Playwright */}
                        <div className={`rounded-lg border p-3 transition-colors ${useLoginConfig ? "border-orange-300 bg-orange-50" : "border-gray-200 bg-gray-50"}`}>
                            <div className="flex items-center justify-between mb-3">
                              <div>
                                <div className="text-xs font-semibold text-gray-800">
                                  Form-based Login
                                </div>
                                <div className="text-[11px] text-gray-500 mt-0.5">
                                  {usePlaywrightFallback
                                    ? "Puppeteer logs in and crawls all pages authenticated."
                                    : "Puppeteer logs in to get session cookies, then Cheerio crawls (faster)."}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => setUseLoginConfig((v) => !v)}
                                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0 ml-3 ${
                                  useLoginConfig ? "bg-orange-500" : "bg-gray-300"
                                }`}
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                                    useLoginConfig ? "translate-x-4" : "translate-x-1"
                                  }`}
                                />
                              </button>
                            </div>
                            {useLoginConfig && (
                              <div className="space-y-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Login Page URL <span className="text-red-500">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    value={loginUrl}
                                    onChange={(e) => setLoginUrl(e.target.value)}
                                    placeholder="https://example.com/login"
                                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-orange-400 outline-none"
                                  />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                      Username Selector <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                      type="text"
                                      value={loginUsernameSelector}
                                      onChange={(e) => setLoginUsernameSelector(e.target.value)}
                                      placeholder="#email"
                                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-orange-400 outline-none"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                      Password Selector <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                      type="text"
                                      value={loginPasswordSelector}
                                      onChange={(e) => setLoginPasswordSelector(e.target.value)}
                                      placeholder="#password"
                                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-orange-400 outline-none"
                                    />
                                  </div>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Submit Button Selector <span className="text-red-500">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    value={loginSubmitSelector}
                                    onChange={(e) => setLoginSubmitSelector(e.target.value)}
                                    placeholder="button[type=submit]"
                                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-orange-400 outline-none"
                                  />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                      Username <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                      type="text"
                                      value={loginUsername}
                                      onChange={(e) => setLoginUsername(e.target.value)}
                                      placeholder="user@example.com"
                                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-orange-400 outline-none"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                      Password <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                      type="password"
                                      value={loginPassword}
                                      onChange={(e) => setLoginPassword(e.target.value)}
                                      placeholder="••••••••"
                                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-orange-400 outline-none"
                                    />
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                      Success URL Pattern{" "}
                                      <span className="text-gray-400 font-normal">(optional)</span>
                                    </label>
                                    <input
                                      type="text"
                                      value={loginSuccessUrlPattern}
                                      onChange={(e) => setLoginSuccessUrlPattern(e.target.value)}
                                      placeholder="/dashboard"
                                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-orange-400 outline-none"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                      Wait For Selector{" "}
                                      <span className="text-gray-400 font-normal">(optional)</span>
                                    </label>
                                    <input
                                      type="text"
                                      value={loginWaitForSelector}
                                      onChange={(e) => setLoginWaitForSelector(e.target.value)}
                                      placeholder=".dashboard-header"
                                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-orange-400 outline-none"
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => setIsModalOpen(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={!seedUrl}
                      onClick={() => setIsModalOpen(false)}
                      className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Save Configuration
                    </button>
                  </div>
                </div>

                {/* Right: Outputs */}
                <div className="w-80 bg-gray-50 overflow-y-auto p-4 space-y-4">
                  <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <div className="w-2 h-2 bg-cyan-500 rounded-full" />
                    Execution Output
                  </h3>

                  {!hasOutputs ? (
                    <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                      <Globe className="w-8 h-8 mb-2 opacity-20" />
                      <p className="text-xs">Available after execution</p>
                    </div>
                  ) : (() => {
                    const cdOn = !!data.enable_change_detection;
                    const isFirstRun = data.outputs.is_first_run;
                    const cs = data.outputs.change_summary;
                    const isSecondRunPlus = cdOn && isFirstRun === false;

                    // ── Shared page card renderer ────────────────────────
                    const renderPageCard = (page: any, key: string, borderCls = "border-gray-200") => (
                      <div key={key} className={`bg-white border ${borderCls} rounded-lg overflow-hidden`}>
                        <button
                          type="button"
                          onClick={() => setExpandedDiffKey(expandedDiffKey === key ? null : key)}
                          className="w-full text-left px-3 py-2.5 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-xs font-medium text-gray-800 truncate">
                                {page.page_title || "Untitled"}
                              </p>
                              <a
                                href={page.source_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="text-[10px] text-cyan-600 truncate mt-0.5 font-mono hover:underline block"
                              >
                                {page.source_url}
                              </a>
                            </div>
                            <ChevronDown className={`w-3.5 h-3.5 text-gray-400 shrink-0 mt-0.5 transition-transform ${expandedDiffKey === key ? "rotate-180" : ""}`} />
                          </div>
                          {page.page_text_snippet && expandedDiffKey !== key && (
                            <p className="text-[10px] text-gray-400 mt-1.5 line-clamp-2">{page.page_text_snippet}</p>
                          )}
                        </button>
                        {expandedDiffKey === key && (
                          <div className="px-3 pb-3 border-t border-gray-100 bg-gray-50">
                            {page.page_full_text ? (
                              <p className="text-[11px] text-gray-700 mt-2 max-h-40 overflow-y-auto whitespace-pre-wrap leading-relaxed">{page.page_full_text}</p>
                            ) : page.page_text_snippet ? (
                              <p className="text-[11px] text-gray-700 mt-2">{page.page_text_snippet}</p>
                            ) : (
                              <p className="text-[11px] text-gray-400 mt-2 italic">No text content</p>
                            )}
                          </div>
                        )}
                      </div>
                    );

                    // ── Shared file card renderer ────────────────────────
                    const renderFileCard = (file: any, i: number) => {
                      const isImage = file.file_type === "IMAGE" || file.mime_type?.startsWith("image/");
                      const isPdf = file.file_type === "PDF";
                      const FileIcon = isImage ? FileImage : isPdf ? FileText : File;
                      const sizeKb = file.file_size_bytes ? Math.round(file.file_size_bytes / 1024) : null;
                      return (
                        <div key={i} className="bg-white border border-gray-200 rounded-lg px-3 py-2.5 flex items-start gap-2.5">
                          <div className="p-1.5 bg-indigo-50 rounded shrink-0 mt-0.5">
                            <FileIcon className="w-3.5 h-3.5 text-indigo-500" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <p className="text-xs font-medium text-gray-800 truncate">{file.file_name || "Unknown file"}</p>
                              {file.file_type && (
                                <span className="text-[9px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-mono shrink-0">{file.file_type}</span>
                              )}
                            </div>
                            <a
                              href={file.source_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-[10px] text-cyan-600 truncate mt-0.5 font-mono hover:underline block"
                            >
                              {file.source_url}
                            </a>
                            {sizeKb !== null && <p className="text-[10px] text-gray-400 mt-0.5">{sizeKb} KB</p>}
                          </div>
                        </div>
                      );
                    };

                    // ── Scraped pages section (Scenario 1 & 2) ──────────
                    const renderScrapedPages = () =>
                      Array.isArray(data.outputs.scraped_pages) && data.outputs.scraped_pages.length > 0 ? (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs font-semibold text-gray-700">Scraped Pages</span>
                            <span className="text-[10px] bg-cyan-100 text-cyan-700 px-1.5 py-0.5 rounded-full font-medium">
                              {data.outputs.scraped_pages.length}
                            </span>
                          </div>
                          <div className="space-y-2">
                            {data.outputs.scraped_pages.map((page: any, i: number) => (
                              <div key={i} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                <button
                                  type="button"
                                  onClick={() => setExpandedPageIdx(expandedPageIdx === i ? null : i)}
                                  className="w-full text-left px-3 py-2.5 hover:bg-gray-50 transition-colors"
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                      <p className="text-xs font-medium text-gray-800 truncate">{page.page_title || "Untitled"}</p>
                                      <a
                                        href={page.source_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="text-[10px] text-cyan-600 truncate mt-0.5 font-mono hover:underline block"
                                      >
                                        {page.source_url}
                                      </a>
                                    </div>
                                    <ChevronDown className={`w-3.5 h-3.5 text-gray-400 shrink-0 mt-0.5 transition-transform ${expandedPageIdx === i ? "rotate-180" : ""}`} />
                                  </div>
                                  {page.page_text_snippet && expandedPageIdx !== i && (
                                    <p className="text-[10px] text-gray-400 mt-1.5 line-clamp-2">{page.page_text_snippet}</p>
                                  )}
                                </button>
                                {expandedPageIdx === i && (
                                  <div className="px-3 pb-3 border-t border-gray-100 bg-gray-50">
                                    {page.page_full_text ? (
                                      <p className="text-[11px] text-gray-700 mt-2 max-h-40 overflow-y-auto whitespace-pre-wrap leading-relaxed">{page.page_full_text}</p>
                                    ) : page.page_text_snippet ? (
                                      <p className="text-[11px] text-gray-700 mt-2">{page.page_text_snippet}</p>
                                    ) : (
                                      <p className="text-[11px] text-gray-400 mt-2 italic">No text content</p>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null;

                    // ── Discovered files section (Scenario 1 & 2) ───────
                    const renderDiscoveredFiles = () =>
                      Array.isArray(data.outputs.discovered_files) && data.outputs.discovered_files.length > 0 ? (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs font-semibold text-gray-700">Discovered Files</span>
                            <span className="text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full font-medium">
                              {data.outputs.discovered_files.length}
                            </span>
                          </div>
                          <div className="space-y-2">
                            {data.outputs.discovered_files.map((file: any, i: number) => renderFileCard(file, i))}
                          </div>
                        </div>
                      ) : null;

                    return (
                      <>
                        {/* ── Status + stats (always) ── */}
                        <div className="bg-white border border-gray-200 rounded-lg p-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">Status</span>
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                              data.outputs.status === "DONE" ? "bg-green-100 text-green-700"
                              : data.outputs.status === "FAILED" ? "bg-red-100 text-red-700"
                              : "bg-blue-100 text-blue-700"
                            }`}>
                              {data.outputs.status ?? "—"}
                            </span>
                          </div>
                          <div className="grid grid-cols-3 gap-2 pt-1 border-t border-gray-100">
                            {[
                              { label: "Pages", value: data.outputs.pages_crawled ?? 0, color: "text-cyan-700" },
                              { label: "Files", value: data.outputs.files_discovered ?? 0, color: "text-indigo-700" },
                              { label: "Failed", value: data.outputs.files_failed ?? 0, color: (data.outputs.files_failed ?? 0) > 0 ? "text-red-600" : "text-gray-500" },
                            ].map(({ label, value, color }) => (
                              <div key={label} className="text-center">
                                <div className={`text-base font-bold ${color}`}>{value}</div>
                                <div className="text-[10px] text-gray-400">{label}</div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* ── Error message (always) ── */}
                        {data.outputs.error_message && (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                            <p className="text-xs text-red-700 break-words">{data.outputs.error_message}</p>
                          </div>
                        )}

                        {/* ── Scenario 2: first run banner ── */}
                        {cdOn && isFirstRun === true && (
                          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-indigo-500 mt-0.5 shrink-0" />
                            <div>
                              <p className="text-xs font-semibold text-indigo-800">First Run — Full Import</p>
                              <p className="text-[11px] text-indigo-600 mt-0.5">No previous data to compare. All pages and files treated as new.</p>
                            </div>
                          </div>
                        )}

                        {/* ── Scenario 3: change summary card ── */}
                        {isSecondRunPlus && cs && (
                          <div className={`rounded-lg border p-3 ${cs.has_changes ? "bg-amber-50 border-amber-200" : "bg-green-50 border-green-200"}`}>
                            <p className={`text-xs font-semibold mb-2 ${cs.has_changes ? "text-amber-800" : "text-green-800"}`}>
                              {cs.has_changes ? "Changes Detected" : "No Changes Detected"}
                            </p>
                            <div className="grid grid-cols-4 gap-1.5">
                              {[
                                { label: "Added", value: cs.added, color: "text-green-700 bg-green-100" },
                                { label: "Removed", value: cs.removed, color: "text-red-700 bg-red-100" },
                                { label: "Modified", value: cs.modified, color: "text-amber-700 bg-amber-100" },
                                { label: "Same", value: cs.unchanged, color: "text-gray-600 bg-gray-100" },
                              ].map(({ label, value, color }) => (
                                <div key={label} className={`rounded-md px-2 py-1.5 text-center ${color}`}>
                                  <div className="text-sm font-bold">{value}</div>
                                  <div className="text-[9px] font-medium">{label}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* ── Scenario 1 & 2: scraped pages + discovered files ── */}
                        {(!cdOn || isFirstRun === true) && renderScrapedPages()}
                        {(!cdOn || isFirstRun === true) && renderDiscoveredFiles()}

                        {/* ── Scenario 3: no changes empty state ── */}
                        {isSecondRunPlus && cs?.has_changes === false && (
                          <div className="flex flex-col items-center justify-center py-8 text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
                            <CheckCircle className="w-7 h-7 mb-2 text-green-400" />
                            <p className="text-xs font-medium text-gray-500">No changes since last run</p>
                            <p className="text-[10px] text-gray-400 mt-0.5">{cs.unchanged} page{cs.unchanged !== 1 ? "s" : ""} unchanged</p>
                          </div>
                        )}

                        {/* ── Scenario 3: diff sections ── */}
                        {isSecondRunPlus && cs?.has_changes && (
                          <>
                            {/* Added pages */}
                            {Array.isArray(data.outputs.added_pages) && data.outputs.added_pages.length > 0 && (
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="w-2 h-2 rounded-full bg-green-500 shrink-0" />
                                  <span className="text-xs font-semibold text-gray-700">Added Pages</span>
                                  <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">{data.outputs.added_pages.length}</span>
                                </div>
                                <div className="space-y-2">
                                  {data.outputs.added_pages.map((page: any, i: number) =>
                                    renderPageCard(page, `added_pages-${i}`, "border-green-200")
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Modified pages */}
                            {Array.isArray(data.outputs.modified_pages) && data.outputs.modified_pages.length > 0 && (
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                                  <span className="text-xs font-semibold text-gray-700">Modified Pages</span>
                                  <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">{data.outputs.modified_pages.length}</span>
                                </div>
                                <div className="space-y-2">
                                  {data.outputs.modified_pages.map((page: any, i: number) =>
                                    renderPageCard(page, `modified_pages-${i}`, "border-amber-200")
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Removed pages */}
                            {Array.isArray(data.outputs.removed_pages) && data.outputs.removed_pages.length > 0 && (
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" />
                                  <span className="text-xs font-semibold text-gray-700">Removed Pages</span>
                                  <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-medium">{data.outputs.removed_pages.length}</span>
                                </div>
                                <div className="space-y-2">
                                  {data.outputs.removed_pages.map((page: any, i: number) =>
                                    renderPageCard(page, `removed_pages-${i}`, "border-red-200")
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Added files */}
                            {Array.isArray(data.outputs.added_files) && data.outputs.added_files.length > 0 && (
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="w-2 h-2 rounded-full bg-green-500 shrink-0" />
                                  <span className="text-xs font-semibold text-gray-700">Added Files</span>
                                  <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">{data.outputs.added_files.length}</span>
                                </div>
                                <div className="space-y-2">
                                  {data.outputs.added_files.map((file: any, i: number) => renderFileCard(file, i))}
                                </div>
                              </div>
                            )}

                            {/* Removed files */}
                            {Array.isArray(data.outputs.removed_files) && data.outputs.removed_files.length > 0 && (
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" />
                                  <span className="text-xs font-semibold text-gray-700">Removed Files</span>
                                  <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-medium">{data.outputs.removed_files.length}</span>
                                </div>
                                <div className="space-y-2">
                                  {data.outputs.removed_files.map((file: any, i: number) => renderFileCard(file, i))}
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default WebScraperNode;
